import { supabase } from '@/integrations/supabase/client';

interface SecurityLogDetails {
  attempted_url: string;
  user_role: string;
  user_agent: string;
  referrer?: string;
  redirect_to?: string;
}

/**
 * Log unauthorized access attempts to activity_log table for security auditing
 * Fire-and-forget - errors are logged but don't block the flow
 */
export async function logUnauthorizedAccessAttempt(
  userId: string,
  tenantId: string | null,
  details: SecurityLogDetails
) {
  try {
    const { error } = await supabase
      .from('activity_log')
      .insert({
        actor_user_id: userId,
        tenant_id: tenantId,
        entity_type: 'security_event',
        entity_id: userId,
        action: 'unauthorized_crm_access_attempt',
        details: {
          ...details,
          timestamp: new Date().toISOString(),
          browser: navigator.userAgent,
        }
      });

    if (error) {
      console.error('[SECURITY_LOG] Failed to log unauthorized access:', error);
    }
  } catch (err) {
    console.error('[SECURITY_LOG] Error logging security event:', err);
  }
}

interface FilePreviewError {
  fileType: 'image' | 'pdf' | 'unknown';
  fileName: string;
  bucket: string;
  path: string;
  errorMessage: string;
  errorCode?: string;
  userId?: string;
  portal: 'charging' | 'crm' | 'inspector';
}

/**
 * Log file preview errors for monitoring and debugging
 */
export async function logFilePreviewError(error: FilePreviewError) {
  try {
    const { error: dbError } = await supabase
      .from('activity_log')
      .insert({
        actor_user_id: error.userId || null,
        tenant_id: null,
        entity_type: 'file_preview_error',
        entity_id: error.path,
        action: 'file_preview_failed',
        details: {
          ...error,
          timestamp: new Date().toISOString(),
          browser: navigator.userAgent,
        }
      });

    if (dbError) {
      console.error('[FILE_PREVIEW_LOG] Failed to log error:', dbError);
    }
  } catch (err) {
    console.error('[FILE_PREVIEW_LOG] Error logging file preview error:', err);
  }
}

interface StorageAccessError {
  operation: 'read' | 'write' | 'delete';
  bucket: string;
  path: string;
  errorMessage: string;
  userId?: string;
}

/**
 * Log storage access errors for monitoring
 */
export async function logStorageAccessError(error: StorageAccessError) {
  try {
    const { error: dbError } = await supabase
      .from('activity_log')
      .insert({
        actor_user_id: error.userId || null,
        tenant_id: null,
        entity_type: 'storage_error',
        entity_id: error.path,
        action: `storage_${error.operation}_failed`,
        details: {
          ...error,
          timestamp: new Date().toISOString(),
          browser: navigator.userAgent,
        }
      });

    if (dbError) {
      console.error('[STORAGE_LOG] Failed to log error:', dbError);
    }
  } catch (err) {
    console.error('[STORAGE_LOG] Error logging storage error:', err);
  }
}
